import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '../components/ui/dialog';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { Plus, Search, MoreVertical, Edit, Trash2, Eye, Building2, Phone, Mail, Upload, Download, FileSpreadsheet, Loader2, ChevronLeft, ChevronRight, AlertTriangle } from 'lucide-react';
import { formatCurrency, truncate, formatRut, getErrorMessage } from '../lib/utils';
import { REGIONES_CHILE, getComunasByRegion } from '../lib/chile';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Clientes = () => {
    const { canEdit, canDelete, user } = useAuth();
    const isSuperadmin = user?.rol === 'superadmin';
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [clientes, setClientes] = useState([]);
    const [empresas, setEmpresas] = useState([]);
    const [search, setSearch] = useState('');
    const [empresaFilter, setEmpresaFilter] = useState('all');
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingCliente, setEditingCliente] = useState(null);
    const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const [page, setPage] = useState(1);
    const PAGE_SIZE = 8;
    const fileInputRef = useRef(null);
    const [formData, setFormData] = useState({
        razon_social: '',
        rut: '',
        direccion: '',
        region: '',
        comuna: '',
        contacto_financiero: '',
        email_cobranza: '',
        email_cc: '',
        telefono: '',
        condicion_pago_dias: 30,
        tipo_facturacion: '',
        observaciones: ''
    });

    useEffect(() => {
        fetchClientes();
        if (isSuperadmin) {
            fetchEmpresas();
        }
        // Reset to first page when filters change
        setPage(1);
    }, [search, empresaFilter]);

    const fetchEmpresas = async () => {
        try {
            const response = await axios.get(`${API_URL}/api/empresas`);
            setEmpresas(response.data || []);
        } catch (error) {
            console.error('Error fetching empresas:', error);
        }
    };

    const fetchClientes = async () => {
        try {
            setLoading(true);
            const params = new URLSearchParams();
            if (search) params.append('search', search);
            if (empresaFilter && empresaFilter !== 'all') {
                params.append('empresa_id', empresaFilter);
            }
            
            const response = await axios.get(`${API_URL}/api/clientes?${params}`);
            setClientes(response.data);
        } catch (error) {
            console.error('Error fetching clientes:', error);
            toast.error('Error al cargar clientes');
        } finally {
            setLoading(false);
        }
    };

    const handleOpenDialog = (cliente = null) => {
        if (cliente) {
            setEditingCliente(cliente);
            setFormData({
                razon_social: cliente.razon_social,
                rut: formatRut(cliente.rut),
                direccion: cliente.direccion || '',
                region: cliente.region || '',
                comuna: cliente.comuna || '',
                contacto_financiero: cliente.contacto_financiero || '',
                email_cobranza: cliente.email_cobranza || '',
                email_cc: cliente.email_cc || '',
                telefono: cliente.telefono || '',
                condicion_pago_dias: cliente.condicion_pago_dias || 30,
                tipo_facturacion: cliente.tipo_facturacion || '',
                observaciones: cliente.observaciones || ''
            });
        } else {
            setEditingCliente(null);
            setFormData({
                razon_social: '',
                rut: '',
                direccion: '',
                region: '',
                comuna: '',
                contacto_financiero: '',
                email_cobranza: '',
                email_cc: '',
                telefono: '',
                condicion_pago_dias: 30,
                tipo_facturacion: '',
                observaciones: ''
            });
        }
        setDialogOpen(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            if (editingCliente) {
                await axios.put(`${API_URL}/api/clientes/${editingCliente.id}`, formData);
                toast.success('Cliente actualizado');
            } else {
                await axios.post(`${API_URL}/api/clientes`, formData);
                toast.success('Cliente creado');
            }
            setDialogOpen(false);
            fetchClientes();
        } catch (error) {
            console.error('Error saving cliente:', error);
            toast.error(getErrorMessage(error, 'Error al guardar cliente'));
        }
    };

    const handleDelete = (cliente) => {
        // Open the in-app confirmation modal (no native window.confirm)
        setDeleteTarget(cliente);
    };

    const confirmDelete = async () => {
        if (!deleteTarget) return;
        setDeleting(true);
        try {
            await axios.delete(`${API_URL}/api/clientes/${deleteTarget.id}`);
            toast.success(`${deleteTarget.razon_social} eliminado junto con toda su información`);
            setDeleteTarget(null);
            fetchClientes();
        } catch (error) {
            console.error('Error deleting cliente:', error);
            toast.error(getErrorMessage(error, 'Error al eliminar cliente'));
        } finally {
            setDeleting(false);
        }
    };

    const handleDownloadTemplate = async () => {
        try {
            const response = await axios.get(`${API_URL}/api/clientes/plantilla/excel`, {
                responseType: 'blob'
            });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'plantilla_clientes.xlsx');
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            toast.success('Plantilla descargada');
        } catch (error) {
            console.error('Error downloading template:', error);
            toast.error('Error al descargar plantilla');
        }
    };

    const handleFileUpload = async (event) => {
        const file = event.target.files?.[0];
        if (!file) return;

        if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
            toast.error('Por favor seleccione un archivo Excel (.xlsx o .xls)');
            return;
        }

        setUploading(true);
        try {
            const formData = new FormData();
            formData.append('file', file);
            
            const response = await axios.post(`${API_URL}/api/clientes/carga-masiva`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });
            
            const { created, skipped, errors } = response.data;
            
            if (created > 0) {
                toast.success(`${created} clientes creados exitosamente`);
            }
            if (skipped > 0) {
                toast.info(`${skipped} clientes omitidos (RUT duplicado)`);
            }
            if (errors && errors.length > 0) {
                toast.error(`${errors.length} errores encontrados`);
            }
            
            setUploadDialogOpen(false);
            fetchClientes();
        } catch (error) {
            console.error('Error uploading file:', error);
            toast.error(getErrorMessage(error, 'Error al procesar archivo'));
        } finally {
            setUploading(false);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };

    const totalPages = Math.max(1, Math.ceil(clientes.length / PAGE_SIZE));
    const currentPage = Math.min(page, totalPages);
    const pageStart = (currentPage - 1) * PAGE_SIZE;
    const pagedClientes = clientes.slice(pageStart, pageStart + PAGE_SIZE);

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-slate-900">Clientes</h1>
                    <p className="text-slate-500 text-sm mt-1">
                        Gestión de clientes y deudores
                    </p>
                </div>
                {canEdit() && (
                    <div className="flex gap-2">
                        <Button 
                            variant="outline" 
                            onClick={() => setUploadDialogOpen(true)}
                            data-testid="bulk-upload-btn"
                        >
                            <Upload size={18} className="mr-2" />
                            Carga Masiva
                        </Button>
                        <Button onClick={() => handleOpenDialog()} data-testid="new-cliente-btn">
                            <Plus size={18} className="mr-2" />
                            Nuevo Cliente
                        </Button>
                    </div>
                )}
            </div>

            {/* Search and Filters */}
            <Card>
                <CardContent className="p-4">
                    <div className="flex flex-col sm:flex-row gap-4">
                        <div className="relative flex-1">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                            <Input
                                placeholder="Buscar por razón social o RUT..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                className="pl-10"
                                data-testid="search-clientes"
                            />
                        </div>
                        {isSuperadmin && (
                            <select
                                value={empresaFilter}
                                onChange={(e) => setEmpresaFilter(e.target.value)}
                                className="px-3 py-2 border border-slate-200 rounded-md bg-white text-sm min-w-[200px]"
                                data-testid="filter-empresa"
                            >
                                <option value="all">Todas las empresas</option>
                                {empresas.map(empresa => (
                                    <option key={empresa.id} value={empresa.id}>
                                        {empresa.razon_social}
                                    </option>
                                ))}
                            </select>
                        )}
                    </div>
                </CardContent>
            </Card>

            {/* Table */}
            <Card>
                <CardContent className="p-0">
                    {loading ? (
                        <div className="flex items-center justify-center h-48">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#5B9A9A]"></div>
                        </div>
                    ) : clientes.length === 0 ? (
                        <div className="flex flex-col items-center justify-center h-48 text-slate-500">
                            <Building2 size={48} className="text-slate-300 mb-2" />
                            <p>No hay clientes registrados</p>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead>
                                    <tr className="table-header border-b">
                                        <th className="text-left py-3 px-4">Razón Social</th>
                                        <th className="text-left py-3 px-4">RUT</th>
                                        {isSuperadmin && (
                                            <th className="text-left py-3 px-4">Empresa</th>
                                        )}
                                        <th className="text-left py-3 px-4 hidden md:table-cell">Contacto</th>
                                        <th className="text-center py-3 px-4">Días Pago</th>
                                        <th className="text-center py-3 px-4">Estado</th>
                                        <th className="text-right py-3 px-4">Acciones</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {pagedClientes.map((cliente) => (
                                        <tr 
                                            key={cliente.id} 
                                            className="table-row"
                                            data-testid={`cliente-row-${cliente.id}`}
                                        >
                                            <td className="table-cell">
                                                <div className="font-medium text-slate-900">
                                                    {truncate(cliente.razon_social, 40)}
                                                </div>
                                                {cliente.email_cobranza && (
                                                    <div className="text-xs text-slate-500 flex items-center gap-1 mt-0.5">
                                                        <Mail size={12} />
                                                        {cliente.email_cobranza}
                                                    </div>
                                                )}
                                            </td>
                                            <td className="table-cell text-slate-600">{cliente.rut}</td>
                                            {isSuperadmin && (
                                                <td className="table-cell" onClick={(e) => e.stopPropagation()}>
                                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-700">
                                                        {cliente.empresa_nombre || 'Sin empresa'}
                                                    </span>
                                                </td>
                                            )}
                                            <td className="table-cell hidden md:table-cell">
                                                {cliente.contacto_financiero && (
                                                    <div className="text-sm">{cliente.contacto_financiero}</div>
                                                )}
                                                {cliente.telefono && (
                                                    <div className="text-xs text-slate-500 flex items-center gap-1">
                                                        <Phone size={12} />
                                                        {cliente.telefono}
                                                    </div>
                                                )}
                                            </td>
                                            <td className="table-cell text-center">
                                                {cliente.condicion_pago_dias} días
                                            </td>
                                            <td className="table-cell text-center">
                                                <Badge className={cliente.activo ? 'badge-success' : 'badge-error'}>
                                                    {cliente.activo ? 'Activo' : 'Inactivo'}
                                                </Badge>
                                            </td>
                                            <td className="table-cell text-right" onClick={(e) => e.stopPropagation()}>
                                                <DropdownMenu>
                                                    <DropdownMenuTrigger asChild>
                                                        <Button variant="ghost" size="icon">
                                                            <MoreVertical size={16} />
                                                        </Button>
                                                    </DropdownMenuTrigger>
                                                    <DropdownMenuContent align="end">
                                                        <DropdownMenuItem onClick={() => navigate(`/clientes/${cliente.id}`)}>
                                                            <Eye size={16} className="mr-2" />
                                                            Ver detalle
                                                        </DropdownMenuItem>
                                                        {canEdit() && (
                                                            <DropdownMenuItem onClick={() => handleOpenDialog(cliente)}>
                                                                <Edit size={16} className="mr-2" />
                                                                Editar
                                                            </DropdownMenuItem>
                                                        )}
                                                        {canDelete() && (
                                                            <DropdownMenuItem 
                                                                onClick={() => handleDelete(cliente)}
                                                                className="text-red-600"
                                                            >
                                                                <Trash2 size={16} className="mr-2" />
                                                                Eliminar
                                                            </DropdownMenuItem>
                                                        )}
                                                    </DropdownMenuContent>
                                                </DropdownMenu>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                    {/* Pagination footer */}
                    {!loading && clientes.length > 0 && (
                        <div className="flex flex-col sm:flex-row items-center justify-between gap-3 px-4 py-3 border-t border-slate-100 bg-slate-50/50">
                            <p className="text-xs text-slate-500" data-testid="pagination-info">
                                Mostrando <span className="font-semibold text-slate-700">{pageStart + 1}</span>
                                {' '}a <span className="font-semibold text-slate-700">{Math.min(pageStart + PAGE_SIZE, clientes.length)}</span>
                                {' '}de <span className="font-semibold text-slate-700">{clientes.length}</span> clientes
                            </p>
                            <div className="flex items-center gap-1">
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                                    disabled={currentPage === 1}
                                    data-testid="pagination-prev"
                                >
                                    <ChevronLeft size={14} />
                                    <span className="hidden sm:inline ml-1">Anterior</span>
                                </Button>
                                <div className="flex items-center gap-1 mx-1">
                                    {Array.from({ length: totalPages }, (_, i) => i + 1)
                                        .filter((n) => n === 1 || n === totalPages || Math.abs(n - currentPage) <= 1)
                                        .reduce((acc, n, idx, arr) => {
                                            if (idx > 0 && n - arr[idx - 1] > 1) acc.push('…');
                                            acc.push(n);
                                            return acc;
                                        }, [])
                                        .map((n, idx) =>
                                            n === '…' ? (
                                                <span key={`gap-${idx}`} className="px-1 text-slate-400 text-xs">…</span>
                                            ) : (
                                                <button
                                                    key={n}
                                                    onClick={() => setPage(n)}
                                                    className={`min-w-[32px] h-8 px-2 rounded-md text-xs font-semibold transition-colors ${
                                                        currentPage === n
                                                            ? 'bg-[#3D7A7A] text-white'
                                                            : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-100'
                                                    }`}
                                                    data-testid={`pagination-page-${n}`}
                                                >
                                                    {n}
                                                </button>
                                            )
                                        )}
                                </div>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                                    disabled={currentPage === totalPages}
                                    data-testid="pagination-next"
                                >
                                    <span className="hidden sm:inline mr-1">Siguiente</span>
                                    <ChevronRight size={14} />
                                </Button>
                            </div>
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Dialog */}
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent className="max-w-2xl">
                    <DialogHeader>
                        <DialogTitle>
                            {editingCliente ? 'Editar Cliente' : 'Nuevo Cliente'}
                        </DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleSubmit}>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-4">
                            <div className="space-y-2">
                                <Label htmlFor="razon_social">Razón Social *</Label>
                                <Input
                                    id="razon_social"
                                    value={formData.razon_social}
                                    onChange={(e) => setFormData({...formData, razon_social: e.target.value})}
                                    required
                                    data-testid="input-razon-social"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="rut">RUT *</Label>
                                <Input
                                    id="rut"
                                    value={formData.rut}
                                    onChange={(e) => setFormData({ ...formData, rut: formatRut(e.target.value) })}
                                    required
                                    placeholder="76.123.456-7"
                                    maxLength={12}
                                    data-testid="input-rut"
                                />
                            </div>
                            <div className="space-y-2 md:col-span-2">
                                <Label htmlFor="direccion">Dirección</Label>
                                <Input
                                    id="direccion"
                                    value={formData.direccion}
                                    onChange={(e) => setFormData({...formData, direccion: e.target.value})}
                                    placeholder="Calle, número, oficina"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="region">Región</Label>
                                <select
                                    id="region"
                                    value={formData.region}
                                    onChange={(e) => setFormData({ ...formData, region: e.target.value, comuna: '' })}
                                    className="flex h-10 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#3D7A7A]/30"
                                    data-testid="select-region"
                                >
                                    <option value="">Selecciona una región…</option>
                                    {REGIONES_CHILE.map((r) => (
                                        <option key={r.nombre} value={r.nombre}>{r.nombre}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="comuna">Comuna</Label>
                                <select
                                    id="comuna"
                                    value={formData.comuna}
                                    onChange={(e) => setFormData({ ...formData, comuna: e.target.value })}
                                    disabled={!formData.region}
                                    className="flex h-10 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#3D7A7A]/30 disabled:bg-slate-50 disabled:text-slate-400"
                                    data-testid="select-comuna"
                                >
                                    <option value="">{formData.region ? 'Selecciona una comuna…' : 'Primero elige una región'}</option>
                                    {getComunasByRegion(formData.region).map((c) => (
                                        <option key={c} value={c}>{c}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="contacto_financiero">Contacto Financiero</Label>
                                <Input
                                    id="contacto_financiero"
                                    value={formData.contacto_financiero}
                                    onChange={(e) => setFormData({...formData, contacto_financiero: e.target.value})}
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="email_cobranza">Email Cobranza</Label>
                                <Input
                                    id="email_cobranza"
                                    type="email"
                                    value={formData.email_cobranza}
                                    onChange={(e) => setFormData({...formData, email_cobranza: e.target.value})}
                                />
                            </div>
                            <div className="space-y-2 md:col-span-2">
                                <Label htmlFor="email_cc">CC (con copia a)</Label>
                                <Input
                                    id="email_cc"
                                    data-testid="cliente-email-cc-input"
                                    value={formData.email_cc}
                                    onChange={(e) => setFormData({...formData, email_cc: e.target.value})}
                                    placeholder="finanzas@empresa.cl, gerente@empresa.cl"
                                />
                                <p className="text-xs text-slate-500">Puedes agregar varios correos separados por coma.</p>
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="telefono">Teléfono</Label>
                                <Input
                                    id="telefono"
                                    value={formData.telefono}
                                    onChange={(e) => setFormData({...formData, telefono: e.target.value})}
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="condicion_pago_dias">Días de Pago</Label>
                                <Input
                                    id="condicion_pago_dias"
                                    type="number"
                                    value={formData.condicion_pago_dias}
                                    onChange={(e) => setFormData({...formData, condicion_pago_dias: parseInt(e.target.value)})}
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="tipo_facturacion">Tipo Facturación</Label>
                                <Input
                                    id="tipo_facturacion"
                                    value={formData.tipo_facturacion}
                                    onChange={(e) => setFormData({...formData, tipo_facturacion: e.target.value})}
                                    placeholder="Ej: Mensual, Por proyecto"
                                />
                            </div>
                            <div className="space-y-2 md:col-span-2">
                                <Label htmlFor="observaciones">Observaciones</Label>
                                <Input
                                    id="observaciones"
                                    value={formData.observaciones}
                                    onChange={(e) => setFormData({...formData, observaciones: e.target.value})}
                                />
                            </div>
                        </div>
                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                                Cancelar
                            </Button>
                            <Button type="submit" data-testid="save-cliente-btn">
                                {editingCliente ? 'Guardar cambios' : 'Crear cliente'}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Upload Dialog */}
            <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <FileSpreadsheet className="text-[#5B9A9A]" size={24} />
                            Carga Masiva de Clientes
                        </DialogTitle>
                    </DialogHeader>
                    <div className="py-4 space-y-4">
                        <p className="text-sm text-slate-600">
                            Descargue la plantilla Excel, complete los datos de sus clientes y súbala para registrarlos de forma masiva.
                        </p>
                        
                        <div className="p-4 bg-[#E8F4F4] border border-[#A5D0D0] rounded-lg">
                            <h4 className="font-medium text-[#1A3D3D] mb-2">Paso 1: Descargar plantilla</h4>
                            <Button 
                                variant="outline" 
                                className="w-full"
                                onClick={handleDownloadTemplate}
                                data-testid="download-template-btn"
                            >
                                <Download size={18} className="mr-2" />
                                Descargar Plantilla Excel
                            </Button>
                        </div>

                        <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                            <h4 className="font-medium text-green-900 mb-2">Paso 2: Subir archivo</h4>
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept=".xlsx,.xls"
                                onChange={handleFileUpload}
                                className="hidden"
                                data-testid="file-input"
                            />
                            <Button 
                                className="w-full bg-green-600 hover:bg-green-700"
                                onClick={() => fileInputRef.current?.click()}
                                disabled={uploading}
                                data-testid="upload-file-btn"
                            >
                                {uploading ? (
                                    <>
                                        <Loader2 size={18} className="mr-2 animate-spin" />
                                        Procesando...
                                    </>
                                ) : (
                                    <>
                                        <Upload size={18} className="mr-2" />
                                        Seleccionar Archivo Excel
                                    </>
                                )}
                            </Button>
                        </div>

                        <div className="text-xs text-slate-500 space-y-1">
                            <p>• Campos obligatorios: Razón Social, RUT</p>
                            <p>• Máximo 500 clientes por carga</p>
                            <p>• Los RUT duplicados serán omitidos</p>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setUploadDialogOpen(false)}>
                            Cerrar
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Confirm Delete Modal (in-app, not browser) */}
            <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && !deleting && setDeleteTarget(null)}>
                <AlertDialogContent data-testid="delete-cliente-modal">
                    <AlertDialogHeader>
                        <div className="mx-auto w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mb-2">
                            <AlertTriangle className="text-red-600" size={24} />
                        </div>
                        <AlertDialogTitle className="text-center">
                            ¿Eliminar a {deleteTarget?.razon_social}?
                        </AlertDialogTitle>
                        <AlertDialogDescription className="text-center space-y-2">
                            <span className="block">
                                Esta acción es <strong className="text-red-600">permanente</strong> y eliminará
                                <strong> en cascada</strong> toda la información asociada al cliente:
                            </span>
                            <span className="inline-block text-left bg-slate-50 border border-slate-200 rounded-lg p-3 text-xs text-slate-700 leading-relaxed">
                                · Todos sus servicios (únicos y recurrentes)<br />
                                · Sus EDPs y comprobantes generados<br />
                                · Sus pagos y registros de cobranza<br />
                                · Notificaciones e historial de auditoría
                            </span>
                            <span className="block text-xs text-slate-500">
                                No se puede deshacer.
                            </span>
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel disabled={deleting} data-testid="delete-cliente-cancel">
                            Cancelar
                        </AlertDialogCancel>
                        <AlertDialogAction
                            onClick={(e) => { e.preventDefault(); confirmDelete(); }}
                            disabled={deleting}
                            className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
                            data-testid="delete-cliente-confirm"
                        >
                            {deleting ? (
                                <><Loader2 size={14} className="mr-2 animate-spin" /> Eliminando…</>
                            ) : (
                                <><Trash2 size={14} className="mr-2" /> Sí, eliminar todo</>
                            )}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
};

export default Clientes;
